import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Space } from "antd";
import React, { useState, useEffect, useCallback } from "react";
import { deleteUser, getUsers } from "../../api/UserApi";
import { UserListForm } from "./UserListForm";
import { useNavigate } from "react-router-dom";
import adnYesNoModal from "../common/adnYesNoModal/AdnYesNoModal";
import AdnStatus from "../common/adnStatus/AdnStatus";
import { getAuthenticatedUserIdWithToken } from "../../api/ApiUtils";
import { tokenKey } from "../../api/OAuth";
import { adnPageSize, appTitle } from "../common/GeneralConfigurations";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import { Link } from "react-router-dom";
import { AppRoutes } from "../../AppRoutes";
import Text from "antd/lib/typography/Text";
import { useTranslation } from "react-i18next";

export const ManageUserListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const title = t("user.title");
  const addName = t("user.addName");
  const namePlaceholder = t("user.placeholders.searchName");
  const [users, setUsers] = useState([]);
  const [currPage, setCurrPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [orderBy, setOrderBy] = useState("");
  const [searchName, setSearchName] = useState("");
  const [searchEmail] = useState("");
  const [userStatus, setUserStatus] = useState("");
  const [nameList, setNameList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `${appTitle} - ${title}`;
  }, [title]);

  const fetchData = useCallback(() => {
    getUsers(
      currPage,
      adnPageSize,
      orderBy,
      searchName,
      searchEmail,
      "",
      userStatus
    )
      .then((res) => {
        if (res && res.data) {
          res.data.result.forEach((u) => {
            if (u.birthDate !== null)
              u.birthDate = JSON.stringify(u.birthDate).substring(1, 11);
          });
          setTotalRecords(res.data.totalRecords);
          setUsers(res.data.result);
        } else {
          setUsers([]);
        }
        setLoading(false);
      })
      .catch((err) => console.log(err));
  }, [currPage, orderBy, searchName, searchEmail, userStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOrderList = (pag, filter, sorter) => {
    sorter.order === "descend"
      ? setOrderBy("-" + sorter.field)
      : sorter.order === "ascend"
      ? setOrderBy(sorter.field)
      : setOrderBy("");
  };

  const handleStatus = (status) => {
    setCurrPage(1);
    if (status === "inactive") {
      setUserStatus("true");
      return;
    }
    if (status === "active") {
      setUserStatus("false");
      return;
    }
    setUserStatus("");
  };

  const handleSelectName = (name) => {
    setSearchName(name);
  };
  const handleSearchName = (value) => {
    value.length >= 3 ? setSearchName(value) : setSearchName("");
  };

  const changeNameList = useCallback(() => {
    if (searchName.length >= 3) {
      const optionValues = [];
      setNameList(
        users
          .map((u) => {
            if (!optionValues.includes(u.name)) {
              optionValues.push(u.name);
              return u;
            }
            return null;
          })
          .filter((u) => u !== null)
      );
      setCurrPage(1);
    } else {
      setNameList([]);
    }
  }, [searchName, users]);

  useEffect(() => {
    changeNameList();
  }, [changeNameList]);

  const handleDelete = async (id) => {
    const res = await deleteUser(id);
    res.status === 200
      ? fetchData()
      : AdnMessageError(t("user.messages.userHasConsultants"));
  };
  const addUser = () => {
    navigate(AppRoutes.user.path);
  };

  const getRoleNames = (roles) => {
    if (roles) {
      let text = "";
      roles.forEach((role) => (text += "#" + role.name + " "));
      return text;
    }

    return "";
  };

  const columns = [
    {
      title: t("user.columns.name"),
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (text, record) => (
        <Link to={AppRoutes.userById.createPath(record.id)}>{record.name}</Link>
      ),
      width: "18%",
    },
    {
      title: t("user.columns.email"),
      dataIndex: "email",
      key: "email",
      sorter: true,
      width: "20%",
    },
    {
      title: t("user.columns.roles"),
      dataIndex: "roles",
      key: "roles",
      render: (_, record) => <Text>{getRoleNames(record.roles)}</Text>,
      width: "18%",
    },
    {
      title: t("user.columns.position"),
      dataIndex: "positionName",
      key: "positionName",
      width: "12%",
    },
    {
      title: t("user.columns.phone"),
      dataIndex: "phone",
      key: "phone",
      width: "14%",
    },
    {
      title: t("user.columns.status"),
      key: "status",
      render: (text, record) => <AdnStatus deleted={record.deleted} />,
      ellipsis: true,
      width: "10%",
    },
    {
      title: t("user.columns.action"),
      key: "action",
      render: (text, record) => (
        <Space size="middle">
          <EditOutlined
            style={{ color: "black" }}
            onClick={() => {
              navigate(AppRoutes.userById.createPath(record.id));
            }}
          />
          {record.deleted ||
            record.id ===
              getAuthenticatedUserIdWithToken(
                localStorage.getItem(tokenKey)
              ) || (
              <DeleteOutlined
                style={{ color: "red" }}
                onClick={() => adnYesNoModal(() => handleDelete(record.id))}
              />
            )}
        </Space>
      ),
      width: "8%",
    },
  ];

  return (
    <UserListForm
      title={title}
      name={addName}
      currPage={currPage}
      totalRecords={totalRecords}
      handleChangePage={setCurrPage}
      users={users}
      columns={columns}
      addUser={addUser}
      handleOrderList={handleOrderList}
      namePlaceholder={namePlaceholder}
      handleSearchName={handleSearchName}
      handleSelectName={handleSelectName}
      nameList={nameList}
      handleStatus={handleStatus}
      loading={loading}
    />
  );
};
